import React, { useState, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import API_CONFIG from '../../apiConfig';

interface ImageUploadProps {
  imageUrls: string[];
  setImageUrls: React.Dispatch<React.SetStateAction<string[]>>;
  title: string;
  isExample?: boolean;
  /** If provided, use this URL for upload (e.g. AI workflow uses ai-workflow/upload) */
  uploadUrl?: string;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ 
  imageUrls, 
  setImageUrls, 
  title,
  isExample = false,
  uploadUrl,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { currentUser } = useAuth();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await uploadImage(files[0]);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await uploadImage(files[0]);
    }
  };

  const uploadImage = async (file: File) => {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setIsUploading(true);

    try {
      // Create FormData (matching photoshoot/tryon format)
      const formData = new FormData();
      formData.append('files', file); // Changed from 'image' to 'files'
      formData.append('userId', currentUser?.uid || 'anonymous'); // Changed fallback from 'user_id' to 'anonymous'

      const endpoint = uploadUrl || API_CONFIG.IMAGE_EDITING_UPLOAD_URL;
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = data.detail || data.message || 'Upload failed. Please try again.';
        alert(message);
        setImageUrls([]);
        return;
      }

      // Handle response (matching photoshoot/tryon format)
      if (data.success) {
        if (data.uploaded && Array.isArray(data.uploaded) && data.uploaded.length > 0) {
          setImageUrls(data.uploaded.map((item: { url: string }) => item.url));
        } else if (data.urls && Array.isArray(data.urls) && data.urls.length > 0) {
          setImageUrls(data.urls);
        } else if (data.url) {
          setImageUrls([data.url]);
        } else {
          alert('Upload failed. No file received.');
          setImageUrls([]);
        }
      } else {
        alert(data.message || 'Upload failed. Please try again.');
        setImageUrls([]);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
      setImageUrls([]);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleRemoveImage = () => {
    setImageUrls([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-text-heading">{title}</h3>

      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          isDragging ? 'border-secondary-500 bg-secondary-100' : 'border-accent-taupe bg-accent-sand hover:border-secondary-400'
        } ${imageUrls.length > 0 ? 'hidden' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => fileInputRef.current?.click()}
      >
        {isUploading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-12 h-12 border-4 border-secondary-500 border-t-transparent rounded-full animate-spin mb-3"></div>
            <p className="text-sm text-text-description">Uploading...</p>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="fas fa-cloud-upload-alt text-secondary-600 text-2xl"></i>
            </div>
            <p className="text-sm text-text-description mb-1">
              <span className="text-secondary-600 font-medium">Click to upload</span> or drag and drop
            </p>
            <p className="text-xs text-text-description">PNG, JPG, WEBP up to 10MB</p>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Preview - fixed container so card size does not grow on upload */}
      {imageUrls.length > 0 && (
        <div className="relative group">
          <div className="w-full h-[180px] max-h-[180px] rounded-lg border border-gray-200 overflow-hidden bg-gray-50 flex items-center justify-center">
            <img
              src={imageUrls[0]}
              alt="Upload preview"
              className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg"
            />
          </div>
          <button
            onClick={handleRemoveImage}
            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            title="Remove image"
          >
            <i className="fas fa-times"></i>
          </button>
          {isExample && (
            <p className="text-xs text-text-description text-center mt-2">
              This is an example image. Upload your own to customize.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;

